import {
  AUTH_JOURNEY_MANIFEST,
  type AuthJourneyId,
  type AuthJourneyStageState,
  type AuthJourneyViewState,
  createAuthJourneyViewState,
} from './journey-manifest';

type StageProjection = Readonly<Record<string, string>>;

const ROUTE_PROJECTIONS: Partial<
  Record<AuthJourneyId, Partial<Record<AuthJourneyId, StageProjection>>>
> = {
  'sign-in': {
    'magic-link': {
      verify: 'verify',
      terms: 'terms',
    },
    'oauth-sign-in': {
      verify: 'verify',
      terms: 'terms',
    },
    'terms-acceptance': {
      terms: 'terms',
    },
  },
  registration: {
    'workspace-setup': {
      'secure-account': 'secure-account',
      workspace: 'workspace',
    },
    'terms-acceptance': {
      terms: 'secure-account',
    },
  },
  'workspace-invite': {
    'terms-acceptance': {
      terms: 'accept',
    },
  },
  'workspace-invite-registration': {
    'workspace-setup': {
      'secure-account': 'secure-account',
    },
    'terms-acceptance': {
      terms: 'account',
    },
  },
  'magic-link': {
    'terms-acceptance': {
      terms: 'terms',
    },
  },
  'oauth-sign-in': {
    'terms-acceptance': {
      terms: 'terms',
    },
  },
};

export function isAuthJourneyContinuation(
  rootJourneyId: AuthJourneyId,
  routeJourneyId: AuthJourneyId
) {
  return (
    rootJourneyId === routeJourneyId ||
    Boolean(ROUTE_PROJECTIONS[rootJourneyId]?.[routeJourneyId])
  );
}

function projectedStateByStage(
  rootJourneyId: AuthJourneyId,
  currentStageId: string,
  currentState: AuthJourneyStageState
) {
  const definition = AUTH_JOURNEY_MANIFEST[rootJourneyId];
  const currentIndex = definition.stages.findIndex(
    stage => stage.id === currentStageId
  );

  return Object.fromEntries(
    definition.stages.map((stage, index) => [
      stage.id,
      index < currentIndex
        ? 'completed'
        : index === currentIndex
          ? currentState
          : stage.conditional
            ? 'conditional-unresolved'
            : 'pending',
    ])
  ) as Record<string, AuthJourneyStageState>;
}

/**
 * Projects a route-local auth state onto the plan chosen at the journey entry.
 * The root manifest—and therefore the numbered total—never changes while the
 * browser moves through compatible callback, terms, invite, or setup routes.
 */
export function resolveAuthJourneyRoutePlan(
  routeState: AuthJourneyViewState,
  rootJourneyId: AuthJourneyId | null
): AuthJourneyViewState {
  if (!rootJourneyId || rootJourneyId === routeState.journeyId) {
    return routeState;
  }

  const projection = ROUTE_PROJECTIONS[rootJourneyId]?.[routeState.journeyId];
  if (!projection || !routeState.currentStageId) {
    return routeState;
  }

  const projectedCurrentStageId = projection[routeState.currentStageId];
  if (!projectedCurrentStageId) {
    return routeState;
  }

  const routeCurrent = routeState.stages.find(
    stage => stage.id === routeState.currentStageId
  );
  const currentState = routeCurrent?.state ?? 'active';

  return createAuthJourneyViewState(
    rootJourneyId,
    projectedCurrentStageId,
    projectedStateByStage(rootJourneyId, projectedCurrentStageId, currentState),
    routeState.outcome
  );
}
